package uz.mint.frontend.api

import uz.mint.frontend.i18n.Locale

/**
 * Phone + one-time code.
 *
 * Registration and login are the same two calls: proving control of a number is the whole
 * credential, so there is no separate sign-up form asking for the number again.
 *
 * The outcomes are modelled as sealed results rather than exceptions because most of them are
 * ordinary things a customer does — mistyping a code, tapping "resend" too soon — and each has
 * its own message on screen.
 */
object AuthApi {

    sealed interface RequestCodeResult {
        /** @param phoneMasked as the server masked it, e.g. `+998 ** *** ** 67`. */
        data class Sent(val phoneMasked: String, val resendAfterSeconds: Int) : RequestCodeResult

        data class TooSoon(val retryAfterSeconds: Int) : RequestCodeResult

        /** The number is not an Uzbek mobile number. */
        data object InvalidPhone : RequestCodeResult

        /** The operator would not take the message; offer Telegram instead of a retry loop. */
        data object DeliveryFailed : RequestCodeResult

        data class Failed(val message: String) : RequestCodeResult
    }

    sealed interface VerifyResult {
        data class Success(val user: Customer, val isNewUser: Boolean) : VerifyResult

        data class WrongCode(val attemptsLeft: Int) : VerifyResult

        /** Expired, already used, or out of attempts — all mean "ask for a new one". */
        data object Expired : VerifyResult

        data object Blocked : VerifyResult

        data class Failed(val message: String) : VerifyResult
    }

    suspend fun requestCode(phone: String, locale: Locale): RequestCodeResult = try {
        // The very first action of a brand-new visitor is a POST, and the CSRF cookie is only
        // issued on a prior request. Without this the first login attempt of a session fails.
        ApiClient.ensureCsrfToken()

        val response = ApiClient.post(
            "/api/auth/otp/request",
            """{"phone":${phone.jsonString()},"locale":"${locale.code}"}""",
        )
        val body = response.json<OtpRequestJson>()

        when {
            response.ok -> RequestCodeResult.Sent(
                phoneMasked = body.phoneMasked ?: "",
                resendAfterSeconds = body.resendAfterSeconds ?: 60,
            )
            response.status == 429 -> RequestCodeResult.TooSoon(body.resendAfterSeconds ?: 60)
            response.status == 400 -> RequestCodeResult.InvalidPhone
            response.status == 502 -> RequestCodeResult.DeliveryFailed
            else -> RequestCodeResult.Failed("HTTP ${response.status}")
        }
    } catch (e: Throwable) {
        RequestCodeResult.Failed(e.message ?: "network error")
    }

    suspend fun verifyCode(phone: String, code: String, locale: Locale): VerifyResult = try {
        val response = ApiClient.post(
            "/api/auth/otp/verify",
            """{"phone":${phone.jsonString()},"code":${code.jsonString()},"locale":"${locale.code}"}""",
        )
        val body = response.json<VerifyJson>()

        when {
            response.ok -> {
                val user = body.user?.toCustomer()
                    ?: return VerifyResult.Failed("response is missing 'user'")
                VerifyResult.Success(user, body.isNewUser == true)
            }
            response.status == 401 -> VerifyResult.WrongCode(body.attemptsLeft ?: 0)
            response.status == 410 -> VerifyResult.Expired
            response.status == 403 -> VerifyResult.Blocked
            else -> VerifyResult.Failed("HTTP ${response.status}")
        }
    } catch (e: Throwable) {
        VerifyResult.Failed(e.message ?: "network error")
    }

    /** Null when nobody is logged in. Anonymous is a normal answer here, not an error. */
    suspend fun currentUser(): Customer? = try {
        val response = ApiClient.get("/api/auth/me")
        if (!response.ok) null else response.json<SessionStateJson>().user?.toCustomer()
    } catch (e: Throwable) {
        null
    }

    suspend fun logout() {
        runCatching { ApiClient.post("/api/auth/logout") }
    }
}

/**
 * The signed-in customer.
 *
 * A subset of the backend's `CustomerDto`, not a mirror of it: the DTO still carries `email`,
 * which no screen shows or edits any more, and a field nothing reads is a field that gets
 * rendered by mistake later.
 */
data class Customer(
    val id: Int,
    val phone: String,
    val phoneFormatted: String,
    val name: String?,
    val locale: String,
)

/**
 * Escapes a value into a JSON string literal.
 *
 * Hand-built JSON bodies are fine for these three flat shapes, but a raw `"$phone"` would let
 * a quote in the input produce a malformed request — and phone fields get pasted into.
 */
internal fun String.jsonString(): String = JSON.stringify(this)

internal external interface CustomerJson {
    val id: Int?
    val phone: String?
    val phoneFormatted: String?
    val name: String?
    val locale: String?
}

internal fun CustomerJson.toCustomer(): Customer? {
    val id = id ?: return null
    val phone = phone ?: return null
    return Customer(
        id = id,
        phone = phone,
        phoneFormatted = phoneFormatted ?: phone,
        name = name,
        locale = locale ?: "ru",
    )
}

private external interface OtpRequestJson {
    val phoneMasked: String?
    val resendAfterSeconds: Int?
    val error: String?
}

private external interface VerifyJson {
    val user: CustomerJson?
    val isNewUser: Boolean?
    val error: String?
    val attemptsLeft: Int?
}

private external interface SessionStateJson {
    val authenticated: Boolean?
    val user: CustomerJson?
}
